import type { EnvironmentEnum } from '@ztechapps/common.contract'
import { getWindow, hasWindow } from '@ztechapps/common.utils'

// Shape of window.__ENV__ — env supplied at container runtime (server.mjs) or by
// the dev Vite plugin, so a single build serves any environment. Client-visible
// values only (public URLs/keys); secrets never leave the backend.
type RuntimeAppEnv = {
  environment?: EnvironmentEnum
  appFullName?: string
  companyName?: string
  recaptchaSiteKey?: string
  apiUrl?: string
  supportEmail?: string
  websiteUrl?: string
}
type RuntimeEnv = {
  app?: RuntimeAppEnv
}

// Runtime env injected into window.__ENV__ — by server.mjs per request in a
// container, or by the dev-only Vite plugin during `nx serve`. Deliberately no
// build-time fallback: a single built artifact must carry no environment values,
// otherwise an image built on beta serves those values after promotion to prod.
const runtimeEnv: RuntimeEnv | undefined = hasWindow()
  ? (getWindow() as unknown as { __ENV__?: RuntimeEnv }).__ENV__
  : undefined
const app = runtimeEnv?.app

export const appSettings = {
  Google: {
    recaptchaSiteKey: app?.recaptchaSiteKey,
  },
  AppFullName: app?.appFullName,
  CompanyName: app?.companyName,
  // Neither server.mjs nor the dev plugin injects supportEmail/websiteUrl (they are
  // not per-environment values — same marketing site and inbox for every env), so
  // these keep a literal default instead of an `import.meta.env.VITE_*` fallback.
  SupportEmail: app?.supportEmail ?? 'manalo.rommel@gmail.com',
  AppUrl: {
    api: app?.apiUrl,
    // The marketing website hosts the canonical privacy/terms pages (the admin app
    // never ships its own copies) — see apps/quickscore/mobile/src/config/appSettings.ts.
    website: app?.websiteUrl ?? 'https://www.quickscoreapp.com',
  },

  // Runtime-only. Never fall back to import.meta.env.MODE — MODE is frozen at build
  // time, so under build-once-on-beta / promote-to-production a fallback would serve
  // the beta value in production. The Restore Db gate fails closed on this being unset.
  environment: app?.environment as EnvironmentEnum,
  // Matches the shared useUser/useLoginFlow default (libs/common/hook/src/useUser/index.ts)
  // so the app no longer needs to override userIdKey/tokenKey anywhere.
  userStorageKeys: {
    userId: 'app-login-userId',
    token: 'app-login-token',
  },
}
